/**
 * RefillTrackingDialog Component
 * 
 * Allows users to set up refill tracking for medications.
 * Tracks pill counts and alerts when running low.
 */

import { useState, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Package,
  AlertTriangle,
  Phone,
  RefreshCw,
  Calculator,
  Calendar,
  Pill,
  Check,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  calculateSupplyRemaining,
  formatSupplyStatus,
  getSupplyStatusColor,
  parseDosesPerDay,
  recordRefill,
  scheduleRefillAlert,
} from '@/services/medicationRefillService';

// Common doses per day options
const DOSES_OPTIONS = [
  { value: '0.5', label: 'Every other day' },
  { value: '1', label: 'Once daily' },
  { value: '2', label: 'Twice daily' },
  { value: '3', label: 'Three times daily' },
  { value: '4', label: 'Four times daily' },
  { value: 'custom', label: 'Custom...' },
];

// Alert threshold options
const ALERT_OPTIONS = [
  { value: 3, label: '3 days before' },
  { value: 5, label: '5 days before' },
  { value: 7, label: '1 week before' },
  { value: 14, label: '2 weeks before' },
  { value: 30, label: '1 month before' },
];

const RefillTrackingDialog = ({
  isOpen,
  onClose,
  medication,
  onSave,
}) => {
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [quantityPrescribed, setQuantityPrescribed] = useState('');
  const [quantityRemaining, setQuantityRemaining] = useState('');
  const [dosesPerDay, setDosesPerDay] = useState('');
  const [customDoses, setCustomDoses] = useState('');
  const [daysSupply, setDaysSupply] = useState('');
  const [refillsRemaining, setRefillsRemaining] = useState('');
  const [alertDays, setAlertDays] = useState(7);
  const [pharmacyPhone, setPharmacyPhone] = useState('');
  const [lastRefillDate, setLastRefillDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [showRefillForm, setShowRefillForm] = useState(false);
  const [refillQuantity, setRefillQuantity] = useState('');

  // Initialize from medication data
  useEffect(() => {
    if (medication) {
      setTrackingEnabled(medication.refill_tracking_enabled || false);
      setQuantityPrescribed(medication.quantity_prescribed?.toString() || '');
      setQuantityRemaining(medication.quantity_remaining?.toString() || '');
      setDaysSupply(medication.days_supply?.toString() || '');
      setRefillsRemaining(medication.refills_remaining?.toString() || '');
      setAlertDays(medication.refill_alert_days || 7);
      setPharmacyPhone(medication.pharmacy_phone || '');
      setLastRefillDate(medication.last_refill_date || '');
      
      // Set doses per day
      const doses = medication.doses_per_day || parseDosesPerDay(medication.frequency);
      if (doses) {
        const matchOption = DOSES_OPTIONS.find(o => o.value === doses.toString());
        if (matchOption) {
          setDosesPerDay(doses.toString());
        } else {
          setDosesPerDay('custom');
          setCustomDoses(doses.toString());
        }
      }
    }
  }, [medication]);

  // Calculate current supply status
  const supplyStatus = useMemo(() => {
    if (!trackingEnabled) return null;
    
    const actualDoses = dosesPerDay === 'custom' 
      ? parseFloat(customDoses) || 0 
      : parseFloat(dosesPerDay) || 0;
    
    return calculateSupplyRemaining({
      quantity_remaining: parseInt(quantityRemaining, 10) || 0,
      quantity_prescribed: parseInt(quantityPrescribed, 10) || 0,
      doses_per_day: actualDoses,
      days_supply: parseInt(daysSupply, 10) || 0,
      last_refill_date: lastRefillDate,
      start_date: medication?.start_date,
      refill_alert_days: alertDays,
    });
  }, [trackingEnabled, quantityRemaining, quantityPrescribed, dosesPerDay, customDoses, daysSupply, lastRefillDate, alertDays, medication]);

  // Handle recording a refill
  const handleRecordRefill = () => {
    const qty = parseInt(refillQuantity, 10);
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    
    const currentRemaining = parseInt(quantityRemaining, 10) || 0;
    setQuantityRemaining((currentRemaining + qty).toString());
    setQuantityPrescribed(qty.toString());
    setLastRefillDate(new Date().toISOString().split('T')[0]);
    
    const currentRefills = parseInt(refillsRemaining, 10) || 0;
    if (currentRefills > 0) {
      setRefillsRemaining((currentRefills - 1).toString());
    }
    
    setShowRefillForm(false);
    setRefillQuantity('');
    toast.success('Refill recorded!');
  };

  // Save all settings
  const handleSave = async () => {
    setSaving(true);
    
    try {
      const actualDoses = dosesPerDay === 'custom' 
        ? parseFloat(customDoses) || null 
        : parseFloat(dosesPerDay) || null;
      
      const updates = {
        ...medication,
        refill_tracking_enabled: trackingEnabled,
        quantity_prescribed: parseInt(quantityPrescribed, 10) || null,
        quantity_remaining: parseInt(quantityRemaining, 10) || null,
        doses_per_day: actualDoses,
        days_supply: parseInt(daysSupply, 10) || null,
        refills_remaining: parseInt(refillsRemaining, 10) || null,
        refill_alert_days: alertDays,
        pharmacy_phone: pharmacyPhone || null,
        last_refill_date: lastRefillDate || null,
      };
      
      // Schedule refill alert if tracking enabled
      if (trackingEnabled && supplyStatus && !supplyStatus.error) {
        await scheduleRefillAlert({
          ...updates,
          id: medication.id,
          name: medication.name,
        });
      }
      
      await onSave(updates);
      toast.success('Refill tracking saved!');
      onClose();
    } catch (error) {
      console.error('Failed to save refill tracking:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!medication) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-emerald-500" />
            Refill Tracking
          </DialogTitle>
          <DialogDescription>
            Track your supply of {medication.name} and get alerts when running low
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-3">
              {trackingEnabled ? (
                <Package className="h-5 w-5 text-emerald-500" />
              ) : (
                <Package className="h-5 w-5 text-slate-400" />
              )}
              <div>
                <Label className="text-base font-medium">
                  Track Refills
                </Label>
                <p className="text-sm text-slate-500">
                  Get notified when your supply is running low
                </p>
              </div>
            </div>
            <Switch
              checked={trackingEnabled}
              onCheckedChange={setTrackingEnabled}
              data-testid="refill-tracking-toggle"
            />
          </div>

          {trackingEnabled && (
            <>
              {/* Current Supply Status */}
              {supplyStatus && !supplyStatus.error && (
                <div className={`p-4 rounded-lg ${getSupplyStatusColor(supplyStatus)}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{formatSupplyStatus(supplyStatus)}</span>
                    {(supplyStatus.isLow || supplyStatus.isCritical) && (
                      <AlertTriangle className="h-5 w-5" />
                    )}
                  </div>
                  {supplyStatus.percentRemaining != null && (
                    <Progress value={supplyStatus.percentRemaining} className="h-2" />
                  )}
                  {supplyStatus.estimatedRunOutDate && (
                    <p className="text-sm mt-2 opacity-80">
                      Estimated to run out: {new Date(supplyStatus.estimatedRunOutDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {/* Quantity Section */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <Pill className="h-4 w-4" />
                      Pills/Doses Remaining
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={quantityRemaining}
                      onChange={(e) => setQuantityRemaining(e.target.value)}
                      placeholder="e.g., 45"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Quantity Prescribed</Label>
                    <Input
                      type="number"
                      min="0"
                      value={quantityPrescribed}
                      onChange={(e) => setQuantityPrescribed(e.target.value)}
                      placeholder="e.g., 90"
                    />
                  </div>
                </div>

                {/* Doses Per Day */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Calculator className="h-4 w-4" />
                    How often do you take it?
                  </Label>
                  <Select value={dosesPerDay} onValueChange={setDosesPerDay}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      {DOSES_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {dosesPerDay === 'custom' && (
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={customDoses}
                      onChange={(e) => setCustomDoses(e.target.value)}
                      placeholder="Doses per day (e.g., 2.5)"
                      className="mt-2"
                    />
                  )}
                </div>

                {/* Days Supply (Alternative) */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-1 text-slate-500">
                    <Calendar className="h-4 w-4" />
                    OR: Days Supply (from pharmacy label)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    value={daysSupply}
                    onChange={(e) => setDaysSupply(e.target.value)}
                    placeholder="e.g., 30"
                  />
                </div>

                {/* Refills Remaining */}
                <div className="space-y-2">
                  <Label>Refills Remaining</Label>
                  <Input
                    type="number"
                    min="0"
                    value={refillsRemaining}
                    onChange={(e) => setRefillsRemaining(e.target.value)}
                    placeholder="e.g., 2"
                  />
                </div>
              </div>

              {/* Alert Settings */}
              <div className="space-y-2">
                <Label>Alert me when</Label>
                <Select 
                  value={alertDays.toString()} 
                  onValueChange={(v) => setAlertDays(parseInt(v, 10))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ALERT_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value.toString()}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Pharmacy Phone */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  Pharmacy Phone (for quick refills)
                </Label>
                <Input
                  type="tel"
                  value={pharmacyPhone}
                  onChange={(e) => setPharmacyPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>

              {/* Last Refill Date */}
              <div className="space-y-2">
                <Label>Last Refill Date</Label>
                <Input
                  type="date"
                  value={lastRefillDate}
                  onChange={(e) => setLastRefillDate(e.target.value)}
                />
              </div>

              {/* Record Refill Button */}
              {!showRefillForm ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowRefillForm(true)}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Record a Refill
                </Button>
              ) : (
                <div className="p-4 bg-emerald-50 rounded-lg space-y-3">
                  <Label className="text-emerald-800">New Refill Quantity</Label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="1"
                      value={refillQuantity}
                      onChange={(e) => setRefillQuantity(e.target.value)}
                      placeholder="Pills received"
                      className="flex-1"
                    />
                    <Button onClick={handleRecordRefill}>
                      <Check className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRefillForm(false)}
                    className="text-slate-500"
                  >
                    Cancel
                  </Button>
                </div>
              )}

              {/* Info Note */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>
                  For automatic tracking, enter either the pill count and frequency, 
                  or the days supply from your pharmacy label.
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RefillTrackingDialog;
